'use client'

import { useEffect, useState, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { Check, X, RefreshCw, CalendarCheck, Hourglass, CheckCircle2, XCircle } from 'lucide-react'
import { GlassCard } from '@/components/ui/glass-card'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { KpiTile } from '@/components/ui/kpi-tile'
import { SectionHeader } from '@/components/ui/section-header'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { cn } from '@/lib/utils'

interface AttendanceRequest {
  id: string
  userId: string
  date: string
  requestedAt: string
  requestedTime: string
  reason: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  approvedCheckIn?: string | null
  user?: { id: string; name: string; email: string; employeeId: string | null }
}

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1))
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

function formatTime12(t: string | null) {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return t || '-'
  const [h, m] = t.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`
}

export function CheckinRequests() {
  const [requests, setRequests] = useState<AttendanceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState<AttendanceRequest | null>(null)
  const [hour, setHour] = useState('10')
  const [minute, setMinute] = useState('00')
  const [period, setPeriod] = useState('AM')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiFetch('/api/checkin-requests')
      const data = await res.json()
      setRequests(data.requests || [])
    } catch (e: any) {
      toast.error(e.message || 'Failed to load check-in requests')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [])

  const kpis = useMemo(() => ({
    pending: requests.filter(r => r.status === 'PENDING').length,
    approved: requests.filter(r => r.status === 'APPROVED').length,
    rejected: requests.filter(r => r.status === 'REJECTED').length,
  }), [requests])

  const openApprove = (r: AttendanceRequest) => {
    const [h, m] = (r.requestedTime || '10:00').split(':').map(Number)
    setHour(String(h % 12 || 12))
    setMinute(String(m).padStart(2, '0'))
    setPeriod(h >= 12 ? 'PM' : 'AM')
    setActive(r)
  }

  const to24 = () => {
    let h = Number(hour) % 12
    if (period === 'PM') h += 12
    return `${String(h).padStart(2, '0')}:${minute}`
  }

  const approve = async () => {
    if (!active) return
    setBusy(true)
    try {
      await apiFetch('/api/checkin-requests', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: active.id, action: 'approve', checkIn: to24() }),
      })
      toast.success('Approved')
      setActive(null)
      load()
    } catch (e: any) {
      toast.error(e.message || 'Approval failed')
    } finally {
      setBusy(false)
    }
  }

  const reject = async (r: AttendanceRequest) => {
    if (!confirm('Reject this check-in request?')) return
    try {
      await apiFetch('/api/checkin-requests', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: r.id, action: 'reject' }),
      })
      toast.success('Rejected')
      load()
    } catch (e: any) {
      toast.error(e.message || 'Reject failed')
    }
  }

  return (
    // No PageShell: only renders nested inside PerformanceAnalytics' shell.
    <div className="space-y-5 md:space-y-6">
      <SectionHeader
        icon={CalendarCheck}
        title="Check-In Approvals"
        description="Review and approve late check-in submissions"
        action={
          <Button size="sm" variant="outline" className="border-neutral-200 dark:border-white/10 text-neutral-800 dark:text-white/80 font-medium" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        <KpiTile label="Pending" value={kpis.pending} icon={Hourglass} tone="amber" />
        <KpiTile label="Approved" value={kpis.approved} icon={CheckCircle2} tone="green" />
        <KpiTile label="Rejected" value={kpis.rejected} icon={XCircle} tone="red" />
      </div>

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : requests.length === 0 ? (
          <EmptyState
            icon={CalendarCheck}
            title="No check-in requests"
            description="Late check-in submissions from employees will show up here."
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[760px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                      <TableHead className="text-neutral-500 dark:text-white/50">Employee</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Date</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Requested Time</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Reason</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Approved Time</TableHead>
                      <TableHead className="text-neutral-500 dark:text-white/50">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map(r => (
                      <TableRow key={r.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:bg-white/5">
                        <TableCell className="text-neutral-900 dark:text-white/90">{r.user?.name || r.userId}</TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">{r.date}</TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">{formatTime12(r.requestedTime)}</TableCell>
                        <TableCell className="max-w-[220px] truncate text-neutral-700 dark:text-white/70">{r.reason || '-'}</TableCell>
                        <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill></TableCell>
                        <TableCell className="text-neutral-800 dark:text-white/80">
                          {r.approvedCheckIn ? formatTime12(r.approvedCheckIn) : '-'}
                        </TableCell>
                        <TableCell>
                          {r.status === 'PENDING' ? (
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" onClick={() => openApprove(r)} title="Approve" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10">
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => reject(r)} title="Reject" className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : <span className="text-neutral-300 dark:text-white/30">-</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {requests.map(r => (
                <GlassCard key={r.id} tone={statusTone(r.status) === 'amber' ? 'amber' : statusTone(r.status) === 'green' ? 'green' : statusTone(r.status) === 'red' ? 'red' : 'default'}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-neutral-900 dark:text-white/90 font-medium truncate">{r.user?.name || r.userId}</p>
                      <p className="text-xs text-neutral-400 dark:text-white/40">{r.date}</p>
                    </div>
                    <StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Requested</p>
                      <p className="text-neutral-800 dark:text-white/80">{formatTime12(r.requestedTime)}</p>
                    </div>
                    <div>
                      <p className="text-neutral-400 dark:text-white/40">Approved</p>
                      <p className="text-neutral-800 dark:text-white/80">
                        {r.approvedCheckIn ? formatTime12(r.approvedCheckIn) : '—'}
                      </p>
                    </div>
                  </div>
                  {r.reason && <p className="mt-2 text-xs text-neutral-600 dark:text-white/60 line-clamp-2">{r.reason}</p>}
                  {r.status === 'PENDING' && (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" className="flex-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25" onClick={() => openApprove(r)}>
                        <Check className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" className="flex-1 bg-rose-500/15 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25" onClick={() => reject(r)}>
                        <X className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </GlassCard>
              ))}
            </div>
          </>
        )}
      </GlassCard>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-neutral-100">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">
              Approve Check-In — {active?.date}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-neutral-700 dark:text-white/70">
              {active?.user?.name || active?.userId} requested check-in at <strong className="text-neutral-900 dark:text-white/90">{formatTime12(active?.requestedTime || null)}</strong>.
              {active?.reason && <span> Reason: <em className="text-neutral-600 dark:text-white/60">{active.reason}</em></span>}
            </p>
            <div>
              <Label className="text-neutral-800 dark:text-white/80">Final Check-In Time</Label>
              <div className="flex gap-2 mt-1">
                <Select value={hour} onValueChange={setHour}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={minute} onValueChange={setMinute}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-neutral-700 dark:text-white/70" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={approve} disabled={busy} className={cn(PRIMARY_BTN, 'font-semibold')}>Approve &amp; Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
